import { useEffect, useMemo, useState } from 'react';
import { Badge } from 'react-bootstrap';
import EyeSvg from '../../common/components/svg/EyeSvg';
import { SALESFORCE_URLS_BY_ENVIRONMENT } from '../../common/common.entities';
import { DateTime } from 'luxon';
import { OrdersMcfTableRow } from '../../common/common.table.interfaces';
import CustomError, { CustomErrorProps } from '../../common/components/CustomError';
import RefreshSvg from '../../common/components/svg/RefreshSvg';
import { useLoading } from '../../loading/LoadingContext';
import { useUserContext } from '../../user/UserContext';
import { apiFetch } from '../../utility/fetch';
import { API_PROD_URL } from '../..';

interface WayfairMcfCancelledProps {
  handleShowContent: any
  handleRowClicked: any
}

export default function WayfairMcfCancelled(props: WayfairMcfCancelledProps){
  const {toggleLoading, changeStep, changeSubStep} = useLoading()
  const {userData} = useUserContext()

  const [ordersMcf, setOrdersMcf]: [OrdersMcfTableRow[], any]  = useState([])
  
  const [refresh, setRefresh] = useState(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS))
  const [error, setError]: [CustomErrorProps, any] = useState({visible: false})

  useEffect(() => {
    const load = async function(){
      changeStep('Wayfair Multi-Channel Fulfillments')
      changeSubStep('Loading cancelled orders')
      toggleLoading(true)

      const response = await apiFetch<OrdersMcfTableRow[]>({
        endpoint: 'azureTables',
        method: 'GET',
        accessToken: userData.accessToken,
        baseUrlOverride: API_PROD_URL,
        params: [
          {key: 'table', value: 'ordersMcf'},
          {key: 'filter', value: `(PartitionKey eq 'Wayfair-MCF-CA' or PartitionKey eq 'Wayfair-MCF-US') and status eq 'cancelled'`}
        ]
      })

      const sortedRows = response.body.sort((a, b) => {
        const dateA = DateTime.fromISO(a.timestamp);
        const dateB = DateTime.fromISO(b.timestamp);
        return dateB.toMillis() - dateA.toMillis(); // last updated first
      });

      setOrdersMcf(sortedRows)

      toggleLoading(false)
    }
    load()
  }, [refresh])

  const view = useMemo(() => {
    if(!ordersMcf) return
    const {handleShowContent} = props
    return <div>
    <CustomError 
      closeError={() => {setError({visible: false})}}
      content={error?.content}
      description={error?.description}
      title={error?.title}
      visible={error?.visible}
    />
    <div>
      <span style={{cursor: 'pointer'}} title='Refresh the table.' onClick={() => {setRefresh(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS))}}><RefreshSvg height={20} /></span><span className='beacon-label'> as of {refresh}</span>
      <span className='beacon-label'> with {ordersMcf.length} cancelled row(s) loaded.</span>
    </div>
    <br/>
    <table className='flexport-table'>
      <thead>
        <tr className="sticky-row-2">
          <th></th>
          <th>MP ORDER NUMBER</th>
          <th>MCF ORDER NUMBER</th>
          <th>UPDATED</th>
          <th>PROCESS STATUS</th>
          <th>WAYFAIR STATUS</th>
          <th>CREATE REQUEST</th>
          <th>CREATE RESPONSE</th>
          <th>STATUS REQUEST</th>
          <th>STATUS RESPONSE</th>
          <th>TRACKING RESPONSE</th>
          <th>ERRORS</th>
        </tr>
      </thead>
      <tbody>
        {ordersMcf.map((row, index) => (
          <tr key={index}>
            <td><span style={{cursor: 'pointer'}} className='beacon-label' onClick={() => {props.handleRowClicked(row)}}>{index + 1} <EyeSvg height={15} /></span></td>
            <td><a className='beacon-link' title={'View Ship Detail in Mission Control'} target="_blank" rel="noopener noreferrer" href={`${SALESFORCE_URLS_BY_ENVIRONMENT['production']}/${row.shippingDetailId}`}>{row.orderNumber}</a></td>
            <td><a className='beacon-link' title={`View Order in Wayfair's Portal`} target="_blank" rel="noopener noreferrer" href={`https://partners.wayfair.com/d/orders/castlegate/mc/${row.fulfillmentOrderRequestId}`}>{row.fulfillmentOrderRequestId}</a></td>
            <td><span>{DateTime.fromISO(row.timestamp).setZone('America/Chicago').toFormat('MM/dd/yyyy t')}</span></td>
            <td>
              <Badge bg={['pending-create', 'pending-shipped', 'pending-tracking'].includes(row.status) ? 'warning' : 'danger'}>{row.status?.toUpperCase()}</Badge>
            </td>
            <td>
              <Badge bg={'secondary'}>{row.wayfairStatus?.toUpperCase()}</Badge>
            </td>

            {/* create request */}
            <td>
              <span 
                title={'View the create request sent, or trying to send, to Wayfair.'} 
                data-title={`Create Fulfillment Order Request`} 
                data-ordernumber={row.orderNumber}
                data-fulfillmentrequestid={row.fulfillmentOrderRequestId}
                data-content={row.createFulfillmentOrderInput}
                onClick={handleShowContent} 
                className='beacon-link'>
                  <EyeSvg height={25}/>
              </span>
            </td>
            {/* create response */}
            <td>
              {row.createFulfillmentOrderResponse && 
                <span 
                  title={'View the create response returned back from Wayfair.'} 
                  data-title={`Create Fulfillment Order Response`} 
                  data-ordernumber={row.orderNumber}
                  data-fulfillmentrequestid={row.fulfillmentOrderRequestId}
                  data-content={row.createFulfillmentOrderResponse}
                  onClick={handleShowContent} 
                  className='beacon-link'>
                    <EyeSvg height={25}/>
                </span>
              }
            </td>
            <td>
              {row.fulfillmentOrderDetailsInput && 
                <span 
                  title={'View the status request sent, or trying to send, to Wayfair.'} 
                  data-title={`Fulfillment Order Details Request`} 
                  data-ordernumber={row.orderNumber}
                  data-fulfillmentrequestid={row.fulfillmentOrderRequestId}
                  data-content={row.fulfillmentOrderDetailsInput}
                  onClick={handleShowContent} 
                  className='beacon-link'>
                    <EyeSvg height={25}/>
                </span>
              }
            </td>
            <td>
              {row.fulfillmentOrderDetailsResponse && 
                <span 
                  title={'View the status response returned back from Wayfair.'} 
                  data-title={`Fulfillment Order Details Response`} 
                  data-ordernumber={row.orderNumber}
                  data-fulfillmentrequestid={row.fulfillmentOrderRequestId}
                  data-content={row.fulfillmentOrderDetailsResponse}
                  onClick={handleShowContent} 
                  className='beacon-link'>
                  <EyeSvg height={25}/>
                </span>
              }
            </td>
            <td>
              {row.warehouseShippingAdviceResponse && 
                <span 
                  title={'View the tracking response returned back from Wayfair.'} 
                  data-title={`Warehouse Shipping Advices Response`} 
                  data-ordernumber={row.orderNumber}
                  data-fulfillmentrequestid={row.fulfillmentOrderRequestId}
                  data-content={row.warehouseShippingAdviceResponse}
                  onClick={handleShowContent} 
                  className='beacon-link'>
                    <EyeSvg height={25}/>
                </span>
              }
            </td>
            <td>
              {row.errors && 
                <span 
                  title={'View the errors associated to any of the requests.'} 
                  data-title={`Errors`} 
                  data-ordernumber={row.orderNumber}
                  data-fulfillmentrequestid={row.fulfillmentOrderRequestId}
                  data-content={row.errors}
                  onClick={handleShowContent} 
                  className='beacon-link'>
                    <EyeSvg height={25}/>
                </span>
              }
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
  }, [ordersMcf])

  return view
}